import { useMemo } from "react";
import { Pressable, StyleSheet, Switch, Text, View } from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { useStudyReminders } from "../../notifications/use-study-reminders";
import { type Colors, useColors } from "../../theme";
import { Section } from "./section";

// Day labels in 0=Sunday … 6=Saturday order to match the stored model.
const DAYS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: "Su" },
  { value: 1, label: "Mo" },
  { value: 2, label: "Tu" },
  { value: 3, label: "We" },
  { value: 4, label: "Th" },
  { value: 5, label: "Fr" },
  { value: 6, label: "Sa" },
];

// A small fixed set of sensible reminder times. A free-form clock
// picker would pull in a native date-picker dependency that doesn't
// render on web — these presets keep the feature one-codebase and
// dependency-light, which is the whole point of the local-only design.
const TIMES: readonly string[] = [
  "07:00",
  "08:00",
  "12:00",
  "18:00",
  "20:00",
  "21:00",
];

function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(":");
  const hour = Number(h);
  const suffix = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${suffix}`;
}

// Settings → Notifications. Mirrors AboutSection's structure: a Section
// wrapper around self-contained rows. Lets the user opt into daily/
// weekly *local* study reminders — scheduled on-device, persisted
// server-side so the choice survives reinstall.
export function NotificationsSection(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { prefs, status, scheduleResult, error, supported, save } =
    useStudyReminders();

  // Local notifications aren't schedulable on web in Expo — be honest
  // and tell the user where this works instead of showing dead controls.
  if (!supported) {
    return (
      <Section title="Notifications">
        <Text style={styles.muted}>
          Study reminders are available in the iOS and Android apps.
        </Text>
      </Section>
    );
  }

  const busy = status === "loading" || status === "saving";

  function toggleEnabled(value: boolean): void {
    void save({ ...prefs, enabled: value });
  }

  function toggleDay(day: number): void {
    const has = prefs.days.includes(day);
    const days = has
      ? prefs.days.filter((d) => d !== day)
      : [...prefs.days, day];
    void save({ ...prefs, days });
  }

  function pickTime(time: string): void {
    void save({ ...prefs, time });
  }

  return (
    <Section title="Notifications">
      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={styles.rowLabel}>Study reminders</Text>
          <Text style={styles.rowHint}>
            A local nudge on the days + time you pick. Nothing leaves your
            device.
          </Text>
        </View>
        <Switch
          value={prefs.enabled}
          onValueChange={toggleEnabled}
          disabled={busy}
          accessibilityLabel="Enable study reminders"
          testID="settings-reminders-toggle"
        />
      </View>

      {prefs.enabled ? (
        <>
          <Text style={styles.groupLabel}>Days</Text>
          <View style={styles.dayRow}>
            {DAYS.map((d) => {
              const active = prefs.days.includes(d.value);
              return (
                <Pressable
                  key={d.value}
                  onPress={() => toggleDay(d.value)}
                  disabled={busy}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: active }}
                  accessibilityLabel={`${d.label} reminder`}
                  testID={`settings-reminder-day-${d.value}`}
                  style={[styles.dayChip, active && styles.dayChipActive]}
                >
                  <Text
                    style={[
                      styles.dayChipText,
                      active && styles.dayChipTextActive,
                    ]}
                  >
                    {d.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Text style={styles.groupLabel}>Time</Text>
          <View style={styles.timeRow}>
            {TIMES.map((t) => {
              const active = prefs.time === t;
              return (
                <Pressable
                  key={t}
                  onPress={() => pickTime(t)}
                  disabled={busy}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: active }}
                  accessibilityLabel={`Remind at ${formatTime(t)}`}
                  testID={`settings-reminder-time-${t}`}
                  style={[styles.timeChip, active && styles.timeChipActive]}
                >
                  <Text
                    style={[
                      styles.timeChipText,
                      active && styles.timeChipTextActive,
                    ]}
                  >
                    {formatTime(t)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      ) : null}

      {scheduleResult === "permission-denied" ? (
        <Text style={styles.warn}>
          Notifications are turned off for PassFirst. Enable them in your
          device settings to get reminders.
        </Text>
      ) : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
    </Section>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: space[4],
    },
    rowText: { flex: 1, gap: space[1] },
    rowLabel: {
      ...textStyle.body,
      fontFamily: fontFamily.semiBold,
      color: colors.textPrimary,
    },
    rowHint: { ...textStyle.bodySmall, color: colors.textMuted },
    groupLabel: {
      ...textStyle.caption,
      color: colors.textMuted,
      marginTop: space[2],
    },
    dayRow: { flexDirection: "row", gap: space[2], flexWrap: "wrap" },
    dayChip: {
      width: 40,
      height: 40,
      borderRadius: radius.pill,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    dayChipActive: {
      borderColor: colors.borderPicked,
      borderWidth: 2,
      backgroundColor: colors.surfacePicked,
    },
    dayChipText: {
      fontFamily: fontFamily.semiBold,
      fontSize: 13,
      color: colors.textPrimary,
    },
    dayChipTextActive: { color: colors.accentSoftText },
    timeRow: { flexDirection: "row", gap: space[2], flexWrap: "wrap" },
    timeChip: {
      paddingVertical: space[2],
      paddingHorizontal: space[3],
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    timeChipActive: {
      borderColor: colors.borderPicked,
      borderWidth: 2,
      paddingVertical: space[2] - 1,
      paddingHorizontal: space[3] - 1,
      backgroundColor: colors.surfacePicked,
    },
    timeChipText: {
      fontFamily: fontFamily.semiBold,
      fontSize: 13,
      color: colors.textPrimary,
    },
    timeChipTextActive: { color: colors.accentSoftText },
    muted: { ...textStyle.bodySmall, color: colors.textMuted },
    warn: { ...textStyle.bodySmall, color: colors.caution },
    error: { ...textStyle.bodySmall, color: colors.error },
  });
}
